import { create } from 'zustand';

import type { ApplicationGate } from '@/features/application/types';
import type { ArrivalSelection } from '@/features/arrival/types';
import type { AssociateStatus } from '@/features/associate/types';
import type { Submission } from '@/features/finish/types';
import type { JobDetail, JobList } from '@/features/jobs/types';
import type { PayCardState } from '@/features/paycard/types';
import type { Language, TranslationKey } from '@/i18n/dictionary';
import { LANGUAGE } from '@/i18n/dictionary';
import { handOffToRecruiter } from '@/services/handoff';
import type { HandoffControl } from '@/services/handoff.contract';
import type { LookupResult, ThrottledResult } from '@/services/identityLookup';
import { DIAGNOSIS, lookupIdentity } from '@/services/identityLookup';

import type { SessionCandidate } from './sessionCandidate';
import { queueNameFromYourName } from './sessionCandidate';

/**
 * The kiosk flow-state machine and the candidate's transient session (convention skill §§ 4, 6).
 *
 * Two Ground Truth rules are enforced here STRUCTURALLY rather than by everyone remembering them
 * (`03-us-kid-1`, decisions 0001 and 0005):
 *
 *  1. **The door only sets copy.** `openCheckIn` consumes the door and stores a `CheckInCopy` — a
 *     translation key. The door value itself is never written to state, so there is nothing downstream to
 *     persist, transmit or diagnose from: the prototype's
 *     `diagnosedOutcome = (entry === 'returning') ? 'returning' : 'new'` has no `entry` to read here.
 *     The claim is about the door VALUE, not about indistinguishability — the two doors necessarily leave
 *     different copy, so `headingKey` is technically a one-bit trace of the tap. What keeps that from
 *     becoming a diagnosis input is that `CheckInCopy` holds exactly one field, a translation key, and a
 *     test pins its key set so a `isReturning`-style flag cannot be smuggled in beside it.
 *  2. **Returning to Welcome IS the end of the candidate's session.** `endCandidateSession` replaces the
 *     whole candidate slice with its initial value instead of nulling named fields, so anything a later
 *     story adds to that slice — a fifth factor, the identity captured from a match — is cleared by
 *     construction rather than by someone remembering to add a line here.
 *
 *     `openCheckIn` replaces that same slice, so the invariant holds on ENTRY as well as on exit and does
 *     not depend on the two happening in order. It has to: between the idle timer firing and React
 *     unmounting the check-in screen the inputs are still live, and one late keystroke, dictation commit or
 *     autofill in that window would otherwise write a factor back into a session that had just been cleared
 *     and carry it to the next candidate.
 *
 *     ⚠️ **With ONE field carried across `openCheckIn`: `sessionCandidate`** [03-us-kad-6j-f]. A door opens
 *     INSIDE the session *Tap to start* began — the session runs from leaving Welcome to returning to it —
 *     so the name the candidate gave on *Have you applied?* is still theirs, and the live socket must go on
 *     reporting it. It is safe to carry: it is written only by a submitted name field, never by a keystroke,
 *     and only while a session is open ({@link isCandidateSessionOpen}), so the late-input window above
 *     cannot write it. `endCandidateSession` still clears it with the rest of the slice.
 */

export const SCREEN = {
  welcome: 'welcome',
  /**
   * The two doors, on their own screen [Figma · 02 · Have you applied?, 12055:10222 and siblings].
   *
   * The design split `01 · Welcome` in two on 2026-09-02: Welcome became a single "Tap to start" door and
   * the new/returning question moved here. KID-1's substance is untouched — the kiosk still asks it before
   * check-in, and the answer still only chooses COPY [decision 0001] — so this is a layout change the frame
   * governs, not a behaviour change the story would have to approve.
   */
  haveYouApplied: 'haveYouApplied',
  checkIn: 'checkIn',
  /** The diagnosis outcome — where the candidate is told which flow they are in (KID-2). */
  routing: 'routing',
  /** The throttle countdown. A screen of its own, not a banner: the form is unavailable while it runs. */
  throttled: 'throttled',
  /** "What brings you in today?" — the static arrival-reason menu (KID-5). */
  application: 'application',
  arrivalReason: 'arrivalReason',
  fitQuestions: 'fitQuestions',
  submitted: 'submitted',
  /**
   * *"We couldn't send your application"* — the Bullhorn write failed [KPW-1c; Figma
   * `Edge cases / E4 · Submission failed (Bullhorn / Workato) — Desktop`]. It takes the place of
   * {@link SCREEN.submitted} when {@link KioskSessionState.submissionFailed} is set, and routes to a person.
   */
  submissionFailed: 'submissionFailed',
  profileUpdate: 'profileUpdate',
  /** The ordered openings list (KJM-1, KJM-3). */
  jobs: 'jobs',
  /** One opening expanded, with its published description (KJM-3, decision 0072). */
  jobDetail: 'jobDetail',
  /**
   * Nothing to offer. A screen of its own rather than an empty list, because the two empty outcomes differ
   * in kind: nothing in radius is a capture-for-later, nothing at all routes to a person (KJM-1).
   */
  noJobs: 'noJobs',
  /** The assignment / submission status card, and the portal deflection on it (KAS-1, KAS-2). */
  associateStatus: 'associateStatus',
  /** Pay-card registration, before the hand-off (KPC-1, decision 0077 put it in this position). */
  i9: 'i9',
  /**
   * *"How you'll get paid"* — what a pay card is, and to go and fetch one
   * [KPC-1; `11 · Why a pay card`, Desktop 12389:15576 · iPad 12389:15677 · Mobile 12389:15778].
   *
   * **On the application journey ONLY, never on the arrival-menu errand.** Three sources agree, and the
   * distinction is the whole point of the screen: it tells a first-time candidate to walk to the box and
   * take a packet, which somebody who came in holding a card they already own does not need to be told.
   *
   * - The frame lives in the file's `FLOW — NEW USER` section, numbered 11, immediately before `12 · Scan`.
   *   The returning flow has its own section (`06a · Pay card (returning)`) and its own first screen —
   *   `R1 · Pay card — link a new one? (ask)`, {@link SCREEN.payCardAsk}, which is not this.
   * - It draws the five-step journey rail at step 4. The returning pay-card frames draw no rail at all.
   * - The prototype, which governs the FLOW [0155], wires `returning-hub → paycard` DIRECTLY and
   *   `paycard-why → paycard`: the menu bypasses this screen.
   *
   * So `ARRIVAL_REASON.payCard` lands on {@link SCREEN.payCardAsk} — never here — and the journey's
   * `submitted → payCard` edge becomes `submitted → payCardWhy → payCard`.
   */
  payCardWhy: 'payCardWhy',
  /**
   * *"Do you want to link a new pay card?"* — the returning candidate's ask [KPC-1a; decision 0077 §3;
   * `Returning user / 07 · R1 · Pay card — link a new one? (ask)`].
   *
   * **On the arrival-menu errand ONLY.** The menu's pay-card row lands here when no card is known to be on
   * file — one that is lands on {@link SCREEN.payCardNeed} instead [Operator, 2026-10-16] — and *Link a new pay card*
   * continues to {@link SCREEN.payCard}; Back is the "no" and returns to the menu. The application tail
   * does not pass through it: a returning candidate who applies takes the new candidate's
   * `payCardWhy → payCard` path, as the prototype draws it [0155].
   */
  payCardAsk: 'payCardAsk',
  /**
   * *"Do you need a new pay card?"* — the menu's pay-card row for a candidate whose record already holds a
   * card [Operator 2026-10-16, «10a- A», to be recorded as decision 0508; `Edge cases / E7 · Pay card — do you need a new
   * one? (returning) — Desktop`].
   *
   * **On the arrival-menu errand ONLY, and only when a card is on file.** The row lands here when
   * `usePayCardOnFile()` is `true` and on {@link SCREEN.payCardAsk} otherwise — `false` and `null` alike,
   * KPC-1f's fail-open. *Yes, I need a new one* continues to {@link SCREEN.payCard}; Back returns to the
   * menu and raises nothing [0177].
   */
  payCardNeed: 'payCardNeed',
  payCard: 'payCard',
  /**
   * *"Your pay card is linked"* — the success state the registration never had
   * [KPC-1; `13 · Pay card linked`, Desktop 12389:15932 · iPad 12389:16019 · Mobile 12389:16106, and
   * `R3 · Pay card linked`, Desktop 12389:17070 · iPad 12389:17282 · Mobile 12389:17492].
   *
   * A successful write used to exit straight to {@link SCREEN.finish} or {@link SCREEN.arrivalReason}, so
   * the candidate did the one thing that decides whether they get paid and was told nothing about it. The
   * prototype states the same defect in as many words: *"the flow had no success state"*.
   *
   * **On BOTH routes, unlike {@link SCREEN.payCardWhy}, and the design says so twice.** Figma draws the
   * screen once in each flow — `13 · Pay card linked` in `09 · Get set up (pay card)` with the five-step
   * rail at step 4 and its `Back button` instance `visible: false`, and `R3 · Pay card linked` in
   * `06a · Pay card (returning)` with no rail and Back visible — identical in copy and structure at all
   * three widths otherwise. The prototype, which governs the FLOW [0155], has ONE `paycard-linked` screen
   * that every successful read reaches (`if(scanState === 'ok'){ showStep('paycard-linked'); }`) and lists
   * it in `HUB_TASK_SCREENS`: *"task screens the hub can send a returning candidate into … their forward
   * button returns to the hub instead of continuing the new-hire chain."*
   *
   * So this is one component with the same `arrival.reason` condition {@link SCREEN.payCard} already
   * carries, and Continue lands exactly where the registration used to: `finish` on the journey, the
   * arrival menu on the errand.
   */
  payCardLinked: 'payCardLinked',
  /**
   * *"Please have a seat"* — the drug-test hand-off [0038, 0129; `13 · Drug test info`].
   *
   * A **destination, not a step** [0129]: it is reached only from the arrival menu, carries no stepper and
   * advances nothing. Until 2026-09-11 the menu's `drugTest` row landed on `finish` instead, where the
   * no-submission branch renders *"Please see a representative"* — defect D1.
   */
  drugTest: 'drugTest',
  /** The welcome-email hand-off and the paperwork message (KAP-5) — `10a · Your next steps`. */
  finish: 'finish',
  /**
   * The closeout: *"That's it for now. Please have a seat."* (KAP-2) — `11 · That's it for now`.
   *
   * A screen of its own, not a block on the finish screen. Decision 0128 was ruled for the separate screen
   * on 2026-09-08, after being ruled the other way and withdrawn: the design file drew this screen into
   * the flow at all three viewports and deleted the callout the alternative consisted of.
   */
  allDone: 'allDone',
} as const;
export type Screen = (typeof SCREEN)[keyof typeof SCREEN];

/**
 * The two front doors. This type exists only as the ARGUMENT to `openCheckIn` — the tap site is the last
 * place in the app that knows which door was used.
 */
export const DOOR = { new: 'new', returning: 'returning' } as const;
export type Door = (typeof DOOR)[keyof typeof DOOR];

/** Everything a door is allowed to change: the copy on the check-in screen. */
export interface CheckInCopy {
  readonly headingKey: TranslationKey;
}

const DOOR_COPY: Record<Door, CheckInCopy> = {
  new: { headingKey: 'checkIn.heading.new' },
  returning: { headingKey: 'checkIn.heading.returning' },
};

/**
 * The four identity factors, as the candidate typed them (decision 0032 — day and month of birth, never the
 * year). They live on the device only for as long as the candidate is standing at it.
 */
export interface CheckInFactors {
  readonly email: string;
  readonly lastName: string;
  readonly birthDay: string;
  readonly birthMonth: string;
  readonly ssnLast4: string;
}
export type CheckInFactorName = keyof CheckInFactors;

const NO_FACTORS: CheckInFactors = {
  email: '',
  lastName: '',
  birthDay: '',
  birthMonth: '',
  ssnLast4: '',
};

/**
 * Whether a lookup is in flight.
 *
 * `pending` exists so Continue can be disabled and a spinner shown — every request needs a visible loading
 * state (convention skill § 6), and on a kiosk a candidate who gets no feedback taps again.
 */
export const LOOKUP_STATUS = { idle: 'idle', pending: 'pending' } as const;
export type LookupStatus = (typeof LOOKUP_STATUS)[keyof typeof LOOKUP_STATUS];

/** The slice that belongs to whoever is standing at the kiosk right now, and to nobody after them. */
interface CandidateSession {
  readonly screen: Screen;
  readonly checkInCopy: CheckInCopy;
  readonly factors: CheckInFactors;
  readonly lookupStatus: LookupStatus;
  /**
   * The diagnosis, once one exists.
   *
   * It carries a Bullhorn candidate id, so it is part of the candidate slice and is cleared by the same
   * whole-slice replacement that clears the factors — the structural rule KID-1 established. A later story
   * that adds another identity-derived field to this slice inherits that clearing for free.
   */
  readonly lookupResult: LookupResult | undefined;
  /** Set only while the throttle is running; drives the countdown screen. */
  readonly throttle: ThrottledResult | undefined;
  /**
   * The factors the api REFUSED on the last lookup — a `400`, whose field the form now names [ISS-147].
   *
   * On the store rather than in `CheckInScreen` because the screen is not mounted when the answer lands:
   * the checking state replaces it while the request is in flight. Empty means "nothing refused". Editing
   * a factor removes it from the list, so the message goes when the candidate acts on it.
   */
  readonly rejectedFactors: readonly CheckInFactorName[];
  /**
   * What the candidate came in for, once they have said (KID-5).
   *
   * Every field below this line belongs to a story built after KID-2 and follows the same rule as the ones
   * above: it lives in THIS slice, so the whole-slice replacement in `openCheckIn` / `endCandidateSession`
   * clears it without anyone adding a line — except `sessionCandidate`, the one field that lasts the whole
   * session and so survives `openCheckIn` (see the module note, rule 2). Their shapes live in their own features rather than here, so a
   * story can refine what it holds without editing this file — which is what lets several be built at once.
   */
  readonly arrival: ArrivalSelection | undefined;
  /**
   * ⭐ **The `POST /arrival-reason` that selection started, while it is in flight** [KID-5d review round 1, M2].
   *
   * The api copies the entry's `arrivalReason` from the visit ONCE, when the hand-off raises it, so a hand-off
   * that overtakes the arrival post lands without it — and the kiosk raises once per session, so no merge
   * fills it later. {@link KioskSessionState.summonRepresentative} waits on this before it posts. ⛔ Not server
   * state and never read for its answer: it is a handle on a request, cleared with the session like the rest.
   */
  readonly arrivalPost: Promise<unknown> | undefined;
  /**
   * Where the profile page's three-check gate stands [decision 0195] — the consent modal, or one of the
   * three refusal screens. `undefined` is the form.
   *
   * **On the store rather than inside `ApplicationScreen`, for two reasons.** It is flow, not decoration:
   * whether a candidate may proceed is a ruling, and 0195 puts the only Bullhorn write behind it. And the
   * design-fidelity gate reaches a screen by seeding this store [`kioskSeed.ts`] — four of these frames
   * (the modal and three refusals, twelve viewports) sit behind a nine-field form, and a state it cannot
   * seed is a state it cannot check, which reads as a passing viewport.
   *
   * ⚠️ `screen` stays `application` throughout, which is deliberate: the draft the candidate typed lives in
   * `ApplicationScreen`'s own state, and a refusal that unmounted the screen would break the promise
   * `Blocked · consent` makes in ISS's own words — *"Nothing you typed has been lost."* Same shape as the
   * checking screen and the different-email prompt, both of which are screens the machine does not name.
   *
   * **A second screen shares the slot:** `ProfileUpdateScreen` uses it for its consent modal and
   * `Blocked · consent` only — asked when the returning candidate's record holds no consent [0507], with no
   * eligibility check [0479] — and `screen` stays `profileUpdate` there for the same reason.
   */
  readonly applicationGate: ApplicationGate | undefined;
  /** The ordered openings, as the backend ranked them (KJM-1). Never re-sorted here. */
  readonly jobs: JobList | undefined;
  /** The opening whose description is open (KJM-3). */
  readonly selectedJob: JobDetail | undefined;
  /** The narrowed status card (KAS-1). Carries no pay field, by the shape of its type. */
  /**
   * Every card the api answered with — one when they are placed, several when they have applied more than
   * once [Operator, 2026-09-08]. A LIST rather than a card because `03-us-kas-1` left multiples
   * unspecified and the ruling filled it with "show what exists".
   */
  readonly associateCards: readonly AssociateStatus[] | undefined;
  /** The submission the tail screens report on (KAP-2, KAP-5, KPC-1). Produced by KPW-1, which is not built. */
  readonly submission: Submission | undefined;
  /**
   * ⭐ **The job orders whose `POST /submissions` is in flight right now** [decision 0186: one submission per
   * job order; ISS-223]. Read and moved only through {@link KioskSessionState.beginSubmission} and
   * {@link KioskSessionState.settleSubmission}.
   *
   * **On the store rather than in `JobDetailScreen`'s own state, because the screen can unmount mid-write.**
   * The header Back is not disabled while the write is in flight. A candidate who went back and reopened the
   * SAME job got a fresh screen whose local flag said "idle", and the second tap sent a second POST for one
   * job order. The store outlives the screen, so the reopened job still reads as being written.
   *
   * Keyed by job order, so another job's write never blocks this one. In this slice, so
   * `endCandidateSession` and `openCheckIn` clear it like everything above: the next candidate never
   * inherits a write that belonged to the person before them.
   */
  readonly submittingJobOrderIds: readonly number[];
  /**
   * ⭐ **The LATEST `POST /submissions` sent for this candidate did not create one** [KPW-1c]. A later
   * created write clears it (`setSubmission`). Rejected, timed
   * out, or the api's 503 for a Bullhorn 200 whose `messages[]` carried an `ERROR` — one fact to the kiosk.
   *
   * ⛔ **Not set when the write was skipped** for want of a candidate id: no Bullhorn write was attempted, so
   * the submission-failed screen — whose copy says *"Your details are saved"* — has nothing to report.
   * In this slice, so the next candidate never inherits it.
   */
  readonly submissionFailed: boolean;
  /** The outcome of pay-card registration — never the proxy id itself (KPC-1). */
  readonly payCard: PayCardState | undefined;
  /**
   * Whether a representative has already been summoned for this candidate.
   *
   * **It is in the candidate slice, so it clears with the session** — a flag that outlived one candidate
   * would silently suppress the next person's hand-off, which is the worst direction for this rule to fail
   * in. That is the same structural clearing every field above gets from `endCandidateSession`.
   *
   * One open call per candidate [KOP-1 AC-14; the Operator, 2026-09-10: *"I cannot ask again until someone
   * takes me out of the queue"*]. A second row for one person is indistinguishable, on a recruiter's
   * screen, from two people waiting.
   */
  readonly recruiterSummoned: boolean;
  /**
   * The *Your name* the raising summon carried, cut as the api cuts it ({@link queueNameFromYourName}), or
   * `undefined` when it carried none or the cut leaves none [03-us-kop-1d AC-5].
   *
   * ⭐ **Display only: the *Need help?* box's *Name* when {@link sessionCandidate} is `null`** [0441 point 5;
   * 03-us-kid-7 O4]. On Welcome there is no session to name ({@link KioskSessionState.enterSessionCandidate}
   * refuses it), yet the `/handoff` body still carries the name and the api names the entry with it, so the
   * box must too. Set only by the summon that raises the entry, like the name itself (`summonRepresentative`).
   */
  readonly summonedName: string | undefined;
  /**
   * ⭐ **The name the queue entry carries, as `/handoff` answered it** (`entryName`) [03-us-kid-7 O4; ISS-38;
   * TASK-093], or `undefined` until an answer carries one — an older api never does.
   *
   * **It wins over {@link sessionCandidate} and {@link summonedName} in the *Need help?* box**, because a press
   * can JOIN an entry that already holds a name (KOP-1d AC-1; 0453), and only the server knows that. Display
   * only, and written only while the session that raised is still the live one.
   */
  readonly queuedEntryName: string | undefined;
  /**
   * ⭐ **Who the live status socket says is here** [03-us-kad-6j-f AC-1a; 0216; 0382 § Ruling 1] — `null`
   * until a name field is SUBMITTED with one, and then the latest one entered. ⛔ Never per keystroke.
   *
   * In this slice so `endCandidateSession` clears it, like everything above: the next candidate starts
   * `null`. ⚠️ Unlike everything above, `openCheckIn` carries it across — it belongs to the SESSION, which
   * began at *Tap to start*, not to the check-in (the module note, rule 2).
   */
  readonly sessionCandidate: SessionCandidate | null;
  /**
   * The screen the Exit question is open on, or `undefined` when it is not asked [ISS-149].
   *
   * ⭐ **One question, two ways to ask it.** The top bar's `Exit` and every Back that would leave the visit
   * open the SAME overlay, and leaving always goes through it [0199; 0340]: NO keeps the candidate where
   * they were, YES is {@link KioskSessionState.endCandidateSession}. Back used to call that action
   * directly, which is how a candidate pressing Back on the job list landed on Welcome with no question
   * and their session gone (ISS UAT RK-058).
   *
   * **On the store because Back is not inside the component that draws the overlay.** `KioskScreen` draws
   * it, and the screens that call `useBack` render `KioskScreen` — their hooks sit ABOVE it, so a
   * context it provided could not reach them, and a second overlay per screen is the thing ISS-149 rules
   * out.
   *
   * ⚠️ **A SCREEN rather than a flag, so the question cannot outlive the screen it was asked on.** The old
   * per-screen `useState` had that property because `App` swaps whole components; a store flag would not,
   * and a lookup or a load landing while the question is up would open it again over the next screen.
   * `KioskScreen` shows it only while this equals the current screen, and clears it when it unmounts.
   *
   * In this slice, so `endCandidateSession` and `openCheckIn` clear it like everything above.
   */
  readonly exitAskedOn: Screen | undefined;
}

const NO_CANDIDATE_SESSION: CandidateSession = {
  screen: SCREEN.welcome,
  // A resting value is needed because copy is not optional; it carries no meaning on Welcome and nothing may
  // branch on it.
  checkInCopy: DOOR_COPY.new,
  factors: NO_FACTORS,
  lookupStatus: LOOKUP_STATUS.idle,
  lookupResult: undefined,
  throttle: undefined,
  rejectedFactors: [],
  arrival: undefined,
  arrivalPost: undefined,
  applicationGate: undefined,
  jobs: undefined,
  selectedJob: undefined,
  associateCards: undefined,
  submission: undefined,
  submittingJobOrderIds: [],
  submissionFailed: false,
  payCard: undefined,
  recruiterSummoned: false,
  summonedName: undefined,
  queuedEntryName: undefined,
  sessionCandidate: null,
  exitAskedOn: undefined,
};

export interface KioskSessionState extends CandidateSession {
  /**
   * ⭐ **What the VENUE wakes up in — the language every candidate session starts in** [03-us-kad-1e AC-14,
   * AC-16; decision 0104]. `en` until a server answer says otherwise [AC-18].
   *
   * ⛔ **It is NOT the same field as {@link KioskSessionState.language}, and a single field cannot be**:
   * *what this venue wakes up in* and *what this candidate chose* are two facts, and the reset at the end of
   * a session needs something to reset TO. Collapsing them is exactly how AC-16 gets built wrong — the
   * previous candidate's English stands, and the Spanish venue serves its second candidate in English.
   *
   * ⭐ **It lives HERE, beside `language` and OUTSIDE the candidate slice**, for three reasons worth
   * writing down. It must outlive a candidate session, so it cannot be in `CandidateSession` — the
   * whole-slice replacement would wipe it. It must be readable SYNCHRONOUSLY by
   * {@link KioskSessionState.endCandidateSession}, which is in this store. And keeping it out of
   * `enrolmentSession` — where the venue's other facts live — preserves that store's one-way dependency on
   * this one (`enrolmentSession` imports `kioskSession`, never the reverse), which is the direction the
   * enrolment module states for its own types.
   *
   * ⛔ **Nothing persists it.** It comes from the server on every load; a `persist` middleware would let a
   * venue's old setting outlive the setting itself [AC-19].
   */
  readonly venueDefaultLanguage: Language;
  /**
   * Chrome language — what is on screen NOW. Deliberately OUTSIDE the candidate slice: it is a display
   * preference, not something the candidate entered, and a candidate who mis-taps a door must not lose
   * their language on the way back (KID-1, "A mis-tap costs nothing").
   *
   * ⚠️ **What CHANGED with [03-us-kad-1e]:** it no longer survives the end of a session unchanged. A
   * candidate's switch holds for their whole session [AC-15] and is then returned to
   * {@link KioskSessionState.venueDefaultLanguage} [AC-16] — ⛔ **never to `LANGUAGE.en`**, which would
   * serve a Spanish venue's second candidate in English just as surely as keeping the first one's choice.
   */
  readonly language: Language;
  /**
   * ⭐ **Which candidate session this is** — a number every end of one moves on [KID-1, KID-2: one visit,
   * one person]. Read through {@link captureCandidateSession}, never compared by hand.
   *
   * The store could say whether a session is open ({@link isCandidateSessionOpen}) but not WHOSE it is, and
   * that cannot tell this visit from the next: Exit → YES → *Tap to start* is open again, for somebody
   * else. A profile save that landed in that window wrote its candidate id into the next person's session
   * and took them to the openings under it.
   *
   * ⚠️ **OUTSIDE the candidate slice, like `language`.** The whole-slice replacement would put it back to
   * the same value at every end, and two sessions sharing one identity is the defect it exists to catch.
   * Moved only by {@link KioskSessionState.endCandidateSession}, which every way back to Welcome is.
   */
  readonly candidateSessionSerial: number;
  /**
   * ⭐ **Which raise a `/handoff` answer belongs to** — the number of the latest summon that sent one, so the
   * entry's name an answer carries is applied only to the raise that asked for it [KID-7 review round 1].
   *
   * Outside the candidate slice for the reason {@link candidateSessionSerial} is: a slice field would go back to
   * the same value at every reset and repeat a number an earlier raise already used. Moved by every raise, by
   * every slice reset (`openCheckIn`, `endCandidateSession`) and by `noteRepresentativeSummoned`, so an answer
   * applies only while its raise is the latest thing that happened to the slice.
   */
  readonly summonSerial: number;
  /**
   * ⭐ **Which lookup the kiosk is waiting on** — the number of the latest `submitLookup`, so an answer can
   * tell whether it is still the one being waited for [KID-2].
   *
   * `lookupStatus` alone could not say. Continue, Back from the checking screen, a corrected typo and
   * Continue again leave the status `pending` with TWO requests in flight, and the first answer to land was
   * applied to whichever attempt was pending at the time. A genuine no-match runs two Bullhorn searches, so
   * `new` is the slowest answer there is: the typo's stale `new` landed on the corrected attempt, the
   * correct `returning` was then dropped, and the candidate was sent to create the duplicate record this
   * capability exists to prevent.
   *
   * ⛔ **Deliberately OUTSIDE the candidate slice**, so `endCandidateSession` and `openCheckIn` never reset
   * it. A counter that restarted with each candidate would give the next person's first attempt the same
   * number as the last person's, and a late answer for the person who walked away would pass the check.
   * It only ever counts up.
   */
  readonly lookupAttempt: number;
  setLanguage: (language: Language) => void;
  /**
   * ⭐ **The venue's answer arriving** — a boot probe or a PIN-in, and nowhere else [AC-14].
   *
   * It moves BOTH fields: the tablet is between candidates at that moment (a load, or a staffer enrolling
   * it), so what the venue wakes up in is also what is on screen. ⛔ **Nothing mid-session calls it**: both
   * entry points sit behind the enrolment store's own guards — `checkEnrolment` probes only out of the
   * interim boot state and `recheckEnrolment` only from the pad — which is what keeps an administrator's
   * change from switching a candidate under their hands [AC-17].
   */
  setVenueDefaultLanguage: (language: Language) => void;
  openCheckIn: (door: Door) => void;
  setFactor: (name: CheckInFactorName, value: string) => void;
  endCandidateSession: () => void;
  /** Runs the lookup and moves the candidate to whichever screen the answer implies. */
  submitLookup: () => Promise<void>;
  /** Leaves the countdown and returns to the form, with the factors intact so they can be corrected. */
  returnToCheckIn: () => void;
  /**
   * Plain navigation, for the screens after the diagnosis.
   *
   * The named actions above carry invariants — a door becomes copy, a session ends, a lookup routes — and
   * `goTo` deliberately carries none: it exists so a feature can move between its own screens without
   * editing this file. It cannot be used to reach `checkIn` or `welcome` in a way that skips their
   * invariants, because those two have named actions and this one is never the way in to them.
   */
  goTo: (screen: Screen) => void;
  /**
   * Opens the Exit question over the current screen. Nothing is cleared: the answer decides that
   * [ISS-149]. The top bar's `Exit` and every Back that would leave the visit call this, never
   * {@link KioskSessionState.endCandidateSession} directly.
   */
  askToExit: () => void;
  /** NO — closes the question and leaves the candidate exactly where they were. */
  dismissExit: () => void;
  setArrival: (arrival: ArrivalSelection) => void;
  /**
   * Holds the arrival post the selection just started, so a hand-off is ordered after it [KID-5d review round 1,
   * M2]. Called by `ArrivalReasonScreen` AFTER it routes — the route never waits on either request.
   */
  holdArrivalPost: (post: Promise<unknown>) => void;
  /**
   * Moves the profile page's gate [decision 0195], or the returning screen's consent [0507]. Carries no
   * invariant of its own, like `goTo`: each screen owns its sequence — `ApplicationScreen`'s form → modal →
   * gate → write, `ProfileUpdateScreen`'s form → modal → write — and enforces its rule at its single write.
   */
  setApplicationGate: (applicationGate: ApplicationGate | undefined) => void;
  setJobs: (jobs: JobList) => void;
  selectJob: (job: JobDetail) => void;
  setAssociateCards: (cards: readonly AssociateStatus[]) => void;
  setSubmission: (submission: Submission) => void;
  /**
   * Marks `jobOrderId`'s submission as in flight, and answers whether the caller may send it: `false` when
   * one for that job order is already in flight in this session [0186]. The check and the mark happen in
   * one synchronous step, the same way `summonRepresentative` sets its flag, so two taps cannot both pass.
   */
  beginSubmission: (jobOrderId: number) => boolean;
  /**
   * The write for `jobOrderId` settled, on success or failure. ⚠️ Call it only while the session that began
   * the write is still the live one ({@link captureCandidateSession}). After Exit, the slice replacement has
   * already cleared the old flag, and the same job order may be the NEXT candidate's write in flight.
   */
  settleSubmission: (jobOrderId: number) => void;
  /** A sent write created no submission [KPW-1c]. Same post-leave rule as `setSubmission`. */
  markSubmissionFailed: () => void;
  /** Records the candidate id the profile page created. See the implementation for why it merges. */
  setCandidateId: (candidateId: number) => void;
  setPayCard: (state: PayCardState) => void;
  /**
   * Puts a representative on this candidate's case — **at most once per candidate session**.
   *
   * ⚠️ **The one rule both halves of the kiosk's promise hang on** [the Operator, 2026-09-11]: *never
   * promise a person without queueing one; never queue one without being asked.* So this is called by an
   * explicit ask, and by the screens that TELL the candidate a representative is coming — and by nothing
   * else. Arriving at a screen that promises nothing must never raise an entry: a *ready for recruiter*
   * row appeared on the Operator's queue at 07:52:33 on 2026-09-11 because `FinishScreen` enqueued on
   * mount after a successful pay-card write, for a candidate who had asked for nobody.
   *
   * The flag is set BEFORE the request and synchronously, which is also what makes a double-invoked
   * effect (`StrictMode`) raise one entry rather than two — the `handedOff` refs the screens used to keep
   * guarded a single screen each and could not see one another.
   *
   * ⚠️ **`name` is the candidate's own, typed into the *Need help?* control, and it does NOT weaken the
   * cap.** A second summon is still a no-op even when it carries a name the first one lacked: KOP-1 AC-14
   * and the Operator's 2026-09-10 ruling — *"I cannot ask again until someone takes me out of the queue"* —
   * are about the PERSON, not about how much is known of them, and a second row for one person is
   * indistinguishable on a recruiter's screen from two people waiting. So the name reaches the queue only
   * when this call is the one that raises the entry. `NeedHelpDialog` is what makes that honest rather than
   * silent: it does not ask for a name it cannot deliver — a candidate who is already queued is shown the
   * confirmation directly, with no field to fill in.
   *
   * ⏳ **What that leaves owed:** somebody summoned anonymously first (the I-9 notice, a screen that
   * promises a representative) and then asking by name reaches a recruiter without their name. Attaching it
   * to the standing entry means an update path the queue has no endpoint for, and merging two calls onto one
   * entry is AC-14's own separate, unbuilt requirement — so it is named here rather than half-built.
   *
   * ⭐ **`control` is the help control the candidate took** [KID-5d], and it travels on the same terms as the
   * name: only when this call is the one that raises the entry. The api writes the reason and the state from
   * it. The caller decides it — `FinishScreen` names the *Ask for help* row's from the HELD arrival selection —
   * and every caller that names none raises exactly the entry it raised before.
   */
  summonRepresentative: (name?: string, control?: HandoffControl) => void;
  /**
   * Records that another path has already put this candidate in front of a representative, so the screens
   * that promise one do not raise a second row.
   *
   * The I-9 notice is the caller: it writes its OWN labelled entry (*I-9 in person*), and the reason label
   * is the only thing telling a recruiter what the person in front of them needs — replacing it, or adding
   * *ready for recruiter* beside it, loses that.
   */
  noteRepresentativeSummoned: () => void;
  /**
   * A name field was submitted [03-us-kad-6j-f § *Which fields give a name*]: it replaces the session's
   * candidate. ⛔ A field that gives none (`null`) leaves the record as it was, and on Welcome there is no
   * session to name — a submit that lands after the session ended changes nothing.
   */
  enterSessionCandidate: (candidate: SessionCandidate | null) => void;
}

export const useKioskSession = create<KioskSessionState>()((set, get) => ({
  /*
   * ⭐ **English until a server answer says otherwise** [03-us-kad-1e AC-18] — the language the product
   * already wakes up in [KID-1; `dictionary.ts:15`], and the same value the api's column defaults to for a
   * venue nobody has set [AC-1]. ⛔ Not a guess to be corrected later: it is the answer when there is none.
   */
  venueDefaultLanguage: LANGUAGE.en,
  language: LANGUAGE.en,
  candidateSessionSerial: 0,
  summonSerial: 0,
  lookupAttempt: 0,
  ...NO_CANDIDATE_SESSION,

  setLanguage: (language) => set({ language }),

  setVenueDefaultLanguage: (language) => set({ venueDefaultLanguage: language, language }),

  /*
   * The door goes in; only copy comes out. Entering check-in also starts the candidate slice from its
   * initial value, so a factor written after the previous session ended — a late keystroke against inputs
   * that are unmounting — cannot reach the next candidate's form. ⚠️ Except `sessionCandidate`, carried
   * across because it belongs to the whole session (the module note, rule 2).
   */
  openCheckIn: (door) =>
    set((state) => ({
      ...NO_CANDIDATE_SESSION,
      screen: SCREEN.checkIn,
      checkInCopy: DOOR_COPY[door],
      sessionCandidate: state.sessionCandidate,
      // The slice goes, so an answer still in flight for a raise in it is not this slice's [`summonSerial`].
      summonSerial: state.summonSerial + 1,
    })),

  setFactor: (name, value) =>
    set((state) => ({
      factors: { ...state.factors, [name]: value },
      rejectedFactors: state.rejectedFactors.filter((refused) => refused !== name),
    })),

  /*
   * ⭐ **The candidate's slice goes, and the language comes back to the VENUE's** [03-us-kad-1e AC-16] —
   * the idle timeout, the *Done* control, the Exit overlay's *YES*, a Back that leaves the flow, and a 401
   * that returns the tablet to the pad. Every one of them is the same store action, which is why the rule
   * lives here rather than in twelve screens.
   *
   * ⛔ **It is not `LANGUAGE.en`.** A Spanish venue that reset to English would fail the criterion in the
   * other direction, and ⛔ **it is not a no-op either**: leaving the previous candidate's choice standing
   * is what served a Spanish venue's SECOND candidate in English.
   *
   * ⚠️ **`language` is written explicitly rather than by adding it to {@link NO_CANDIDATE_SESSION}**, and
   * the difference is load-bearing: that constant is also spread by `openCheckIn`, where the language must
   * ⛔ **NOT** move [AC-15] — a candidate who switched on Welcome and then picked a door would be flipped
   * back mid-journey. One constant cannot mean both.
   *
   * `candidateSessionSerial` moves here for the same reason, and only here: a write still in flight for
   * the person who just left must find the session it started in gone [{@link captureCandidateSession}].
   */
  endCandidateSession: () =>
    set((state) => ({
      ...NO_CANDIDATE_SESSION,
      language: state.venueDefaultLanguage,
      candidateSessionSerial: state.candidateSessionSerial + 1,
      summonSerial: state.summonSerial + 1,
    })),

  /**
   * The first — and, in this story, only — request of a candidate's session.
   *
   * Every outcome lands on a working screen, including the ones nobody planned for. A transport failure is
   * routed to the same neutral hand-off as an ambiguous match, because the candidate's options are the same
   * either way and inventing a third screen for "the network broke" helps nobody standing in a lobby.
   * Crucially it is NEVER routed to `new`: letting an outage through as a new applicant creates the
   * duplicate application this capability exists to prevent.
   */
  submitLookup: async () => {
    const { factors, lookupStatus } = get();
    // A second tap while one is in flight must not fire a second lookup — on the throttled path that would
    // burn two of the candidate's five attempts for one intent.
    if (lookupStatus === LOOKUP_STATUS.pending) return;
    if (!areAllFactorsPresent(factors)) return;

    // Taken BEFORE the await: it is this request's number, whatever the store says by the time it answers.
    const attempt = get().lookupAttempt + 1;
    set({ lookupAttempt: attempt, lookupStatus: LOOKUP_STATUS.pending, rejectedFactors: [] });
    const outcome = await lookupIdentity(factors);

    /*
     * ⛔ **An answer applies only to the attempt that asked for it** — checked once, here, ahead of every
     * outcome below, so a stale diagnosis, `400`, `429` and outage are all dropped the same way.
     *
     * Both halves are needed. `pending` alone is what this used to check, and it passes whenever SOME lookup
     * is in flight: Back on the checking screen, a corrected typo and Continue again put a second request
     * behind the first, and the first answer was applied to the second attempt — then the second answer was
     * dropped, because the first had already set the status back to idle (see `lookupAttempt`). The attempt
     * alone would let an answer through after Back with nothing resubmitted, or after the session ended,
     * and yank the candidate off whatever screen they went to, seconds later, with no way to tell why.
     */
    if (!isAwaitedAttempt(get(), attempt)) return;

    /*
     * Every outcome is named here, and the `never` makes a fifth one a TYPECHECK failure rather than a
     * silent fall into whichever branch came last — which is how a `400` once became the hand-off.
     */
    switch (outcome.kind) {
      case 'throttled':
        set({
          lookupStatus: LOOKUP_STATUS.idle,
          screen: SCREEN.throttled,
          throttle: outcome.result,
        });
        return;
      /*
       * ⛔ **A refused factor is never the neutral hand-off** [ISS-147; 0220 L1]. The hand-off tells the
       * candidate a representative has been let know, and nothing raises an entry on this path — so a typo
       * in an email used to end in a promise of a person nobody was told about. The api said which field;
       * the form stays up with everything they typed and says so. `screen` is not touched: it is still
       * `checkIn`, and dropping `pending` is what brings the form back.
       */
      case 'rejected':
        set({ lookupStatus: LOOKUP_STATUS.idle, rejectedFactors: outcome.factors });
        return;
      case 'diagnosed':
        set({
          lookupStatus: LOOKUP_STATUS.idle,
          screen: SCREEN.routing,
          lookupResult: outcome.result,
        });
        return;
      // The transport failed: fail closed to the hand-off, never to `new` (see the note above this action).
      case 'unreachable':
        set({
          lookupStatus: LOOKUP_STATUS.idle,
          screen: SCREEN.routing,
          // No answer means no read [0440: `null` — the read did not happen], and nothing retries it [0029].
          lookupResult: { diagnosis: DIAGNOSIS.assistance, requiredCredentialsComplete: null },
        });
        return;
      default: {
        const unhandled: never = outcome;
        return unhandled;
      }
    }
  },

  /*
   * Clears `lookupStatus` too, and that is load-bearing rather than tidiness.
   *
   * The checking screen now offers the Back its frame always drew, and this is what it calls. `submitLookup`
   * discards its own result when the status is no longer `pending`, so leaving is what makes the in-flight
   * answer harmless — without this line the guard never fires and the candidate is dragged onward seconds
   * after walking away. Caught by the test that asserts exactly that. If they then submit again, the new
   * attempt's number is what keeps the old answer out (`lookupAttempt`).
   */
  returnToCheckIn: () =>
    set({
      screen: SCREEN.checkIn,
      throttle: undefined,
      lookupResult: undefined,
      lookupStatus: LOOKUP_STATUS.idle,
      rejectedFactors: [],
    }),

  goTo: (screen) => set({ screen }),
  askToExit: () => set((state) => ({ exitAskedOn: state.screen })),
  dismissExit: () => set({ exitAskedOn: undefined }),
  setArrival: (arrival) => set({ arrival }),
  holdArrivalPost: (arrivalPost) => set({ arrivalPost }),
  setApplicationGate: (applicationGate) => set({ applicationGate }),
  setJobs: (jobs) => set({ jobs }),
  selectJob: (selectedJob) => set({ selectedJob }),
  setAssociateCards: (associateCards) => set({ associateCards }),
  /**
   * Records the Bullhorn candidate id the profile page just created [KAP-7; decision 0033].
   *
   * It merges into the existing lookup result rather than replacing it: the diagnosis that got the
   * candidate here is still true, and everything downstream — the openings list, the submission, the
   * recruiter queue — selects `lookupResult.candidateId` and would otherwise still see a stranger.
   *
   * ⭐ **A record created in this visit holds no pay card** [KPC-1f AC-5; 0470 § Derived]: when the lookup
   * matched nobody there was no id to merge onto, so the record is the one just made and `payCardOnFile` is
   * `false` with no read. An update keeps the lookup's own answer.
   */
  setCandidateId: (candidateId) =>
    set((state) => ({
      lookupResult: {
        ...(state.lookupResult ?? { diagnosis: DIAGNOSIS.new, requiredCredentialsComplete: null }),
        ...(state.lookupResult?.candidateId === undefined ? { payCardOnFile: false } : {}),
        candidateId,
      },
    })),
  // The latest write decides [KPW-1c]: a created submission clears an earlier failure in this session.
  setSubmission: (submission) => set({ submission, submissionFailed: false }),
  beginSubmission: (jobOrderId) => {
    if (get().submittingJobOrderIds.includes(jobOrderId)) return false;
    set((state) => ({ submittingJobOrderIds: [...state.submittingJobOrderIds, jobOrderId] }));
    return true;
  },
  settleSubmission: (jobOrderId) =>
    set((state) => ({
      submittingJobOrderIds: state.submittingJobOrderIds.filter((id) => id !== jobOrderId),
    })),
  markSubmissionFailed: () => set({ submissionFailed: true }),
  setPayCard: (payCard) => set({ payCard }),

  /*
   * The rule lives HERE rather than in each screen because it is a rule ABOUT THE CANDIDATE, not about a
   * screen: three screens can each be sure they raised one entry and still leave three on the queue.
   *
   * Fire-and-forget, like the write it makes [BR-K7, decision 0046]: a recruiter's list being unreachable
   * is a staff problem, and nobody is held at an unattended kiosk over it.
   */
  summonRepresentative: (name, control) => {
    if (get().recruiterSummoned) return;
    const raise = get().summonSerial + 1;
    set({
      recruiterSummoned: true,
      summonedName: name === undefined ? undefined : queueNameFromYourName(name),
      summonSerial: raise,
    });
    const { arrivalPost, lookupResult } = get();
    /*
     * ⭐ **The answer may name the entry, and the box shows THAT name** [ISS-38; 03-us-kid-7 O4]: a press that
     * joined an entry already holding a name reads that name on the recruiter's row, not the typed one. Kept
     * only while THIS raise is still the latest and its slice has not been replaced — a late answer must not
     * name a later raise's box, or the next candidate's.
     */
    /*
     * ⭐ **A write that did not land gives the control back** [0500 point 3; 0220 L1 — the promise must have an
     * entry behind it, so a write that did not land hands the human control back]: `recruiterSummoned` was set
     * before the write, so a failed one would leave every later *Get help* / *Need help?* press sending nothing
     * while the queue holds no entry. Only the flag is reset — the next human tap raises again. ⛔ Never retried
     * here: the write is fire-and-forget [0046; BR-K7; 0457], so a retry is a human's tap, not the store's. (A
     * second raise in the same visit joins the visit's entry on the server [KOP-1d AC-1, the key is the
     * server's], so a tap after a write that timed out but landed adds no row.) ⛔ No error state either: an
     * overlay already open on O4 stays on it, and `summonedName` stays so its box does not change [0457 point 4]
     * (the next raise sets it afresh).
     *
     * The serial guard runs FIRST: a slice reset, a later raise, or `noteRepresentativeSummoned` moves
     * `summonSerial`, and an answer for an older raise then changes nothing — its failure included.
     */
    const send = (): Promise<void> =>
      handOffToRecruiter(lookupResult?.candidateId, name, control).then(({ landed, entryName }) => {
        if (get().summonSerial !== raise) return;
        if (!landed) {
          set({ recruiterSummoned: false });
          return;
        }
        if (entryName !== undefined) set({ queuedEntryName: entryName });
      });
    /*
     * ⭐ **Every summon reaches the server AFTER the arrival post it follows has settled** [KID-5d review round 1,
     * M2]. The api reads the visit once per raise and copies `arrivalReason` from it, so the *Ask for help* row's
     * entry carries `somethingElse` only if the selection got there first. Nothing awaits THIS: the candidate is
     * already on the destination, and the wait is bounded by the arrival post's own 8 s timeout.
     *
     * ⚠️ **A failed arrival post still hands off** — its outcome is swallowed here. The entry then carries no
     * arrival reason: degraded, and accepted, because a person on the queue without a why is far better than no
     * person [BR-K7; 0046].
     */
    void (arrivalPost === undefined ? send() : arrivalPost.catch(() => undefined).then(send));
  },

  /*
   * ⭐ **Moves `summonSerial` too**, so a raise still in flight is no longer the latest: if its write then fails,
   * its reset is ignored and the flag the I-9 notice set stands. Without this, that reset cleared the note and the
   * closing screen raised a second, differently labelled row for the same person.
   */
  noteRepresentativeSummoned: () =>
    set((state) => ({ recruiterSummoned: true, summonSerial: state.summonSerial + 1 })),

  enterSessionCandidate: (candidate) => {
    if (candidate === null || !isCandidateSessionOpen(get())) return;
    set({ sessionCandidate: candidate });
  },
}));

/**
 * ⭐ **The one owner of "a candidate session is open"** [03-us-kad-6j-f § *Where a candidate session starts
 * and ends*]: exactly while `screen` is off `welcome`. *Tap to start* is the only way off it, and every way
 * back is {@link KioskSessionState.endCandidateSession}. Read by the store (a name is held only inside a
 * session) and by the live status socket (what it reports).
 */
export function isCandidateSessionOpen(state: Pick<KioskSessionState, 'screen'>): boolean {
  return state.screen !== SCREEN.welcome;
}

/**
 * ⭐ **Holds on to the candidate session it is called in, and answers whether it is still the live one**
 * [KID-1, KID-2: one visit, one person].
 *
 * Call it BEFORE an `await` and apply the answer only while the result says `true`. A fresh read after the
 * `await` cannot do this job: by then the person who asked may have pressed Exit, and the session open on
 * the screen — {@link isCandidateSessionOpen} included — is the next person's.
 */
export function captureCandidateSession(): () => boolean {
  const serial = useKioskSession.getState().candidateSessionSerial;
  return () => useKioskSession.getState().candidateSessionSerial === serial;
}

/**
 * Whether an answer for `attempt` is still the one being waited for: it is the latest lookup, and nobody has
 * left it (`returnToCheckIn`, `endCandidateSession`) since it was sent.
 */
function isAwaitedAttempt(
  state: Pick<KioskSessionState, 'lookupAttempt' | 'lookupStatus'>,
  attempt: number,
): boolean {
  return state.lookupAttempt === attempt && state.lookupStatus === LOOKUP_STATUS.pending;
}

/**
 * All four factors present [decision 0032 — "please fill in all four"].
 *
 * Presence only. The SHAPE of each factor — an email, exactly four digits — is checked on the form before
 * it ever calls this [`features/checkin/validation.ts`, ISS-147], and a shape the api still refuses comes
 * back as `rejectedFactors`. The MATCH rule stays the api's alone: nothing here decides who a person is.
 */
export function areAllFactorsPresent(factors: CheckInFactors): boolean {
  return (Object.keys(NO_FACTORS) as CheckInFactorName[]).every(
    (name) => factors[name].trim().length > 0,
  );
}
